import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { Dispatch, SetStateAction, useEffect } from "react"
import { Config, formatPrice } from "./CarConfiguration"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const TopBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ColorsFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
`

const StyledFieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`

const StyledLegend = styled.legend`
  padding: 0;
  margin-bottom: 10px;
`

const ColorLabel = styled.label<{ selected: boolean }>`
  display: inline-block;
  cursor: pointer;
  width: ${({ selected }) => (selected ? "56px" : "40px")};
  height: ${({ selected }) => (selected ? "56px" : "40px")};
  transition:
    width 0.2s,
    height 0.2s;
`

const ColorCircle = styled.span<{ $color: string; selected: boolean }>`
  display: block;
  width: 100%;
  height: 100%;
  border: 1px solid #6f6f6f;
  background-color: ${({ $color }) => $color || "transparent"};
  border-radius: 50%;
  box-sizing: border-box;

  ${({ selected }) =>
    selected &&
    `
    border-width: 2px;
  `}
`

const HiddenRadio = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;

  &:focus + ${ColorCircle} {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`

export default function SecondStage({
  markComplete,
  colors,
  config,
  setConfig,
}: {
  markComplete: () => void
  colors: {
    color: string
    label: string
    price: number
  }[]
  config: Config
  setConfig: Dispatch<SetStateAction<Config>>
}) {
  // Auto-select when there's only one color option
  useEffect(() => {
    if (colors.length === 1 && config.color === null) {
      setConfig((prev) => ({ ...prev, color: 0 }))
      markComplete()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (index: number) => {
    setConfig((prev) => ({
      ...prev,
      color: index,
    }))
    markComplete()
  }

  const getColorAriaLabel = (colorOption: {
    color: string
    label: string
    price: number
  }) => {
    const priceText =
      colorOption.price !== 0
        ? `מחיר ${formatPrice(colorOption.price)}`
        : "ללא עלות"
    return `${colorOption.label}, ${priceText}`
  }

  const selectedColor =
    config.color !== null && colors.length > 0 ? colors[config.color] : null
  return (
    <StyledLayout>
      <StyledFieldset>
        <StyledLegend>
          <Typography variant="h2" component="h2">
            בחירת צבע
          </Typography>
        </StyledLegend>
        <ContentLayout>
          {selectedColor !== null && (
            <TopBox>
              <Typography variant="body1">{selectedColor.label}</Typography>
              <Typography variant="body1" aria-hidden="true">
                {selectedColor.price !== 0
                  ? formatPrice(selectedColor.price)
                  : "ללא עלות"}
              </Typography>
            </TopBox>
          )}
          <ColorsFlex role="radiogroup" aria-label="בחירת צבע רכב">
            {colors.map((el, i) => (
              <ColorLabel key={i} selected={config.color === i}>
                <HiddenRadio
                  type="radio"
                  name="color"
                  value={i}
                  checked={config.color === i}
                  onChange={() => handleSelect(i)}
                  aria-label={getColorAriaLabel(el)}
                />
                <ColorCircle
                  $color={el.color}
                  selected={config.color === i}
                  aria-hidden="true"
                />
              </ColorLabel>
            ))}
          </ColorsFlex>
        </ContentLayout>
      </StyledFieldset>
    </StyledLayout>
  )
}
